import { Badge } from '@/ui/Badge';
import { cn } from '@/lib/cn';
import type { Canal, EstadoComunicacion } from './api';

/**
 * piezas.tsx — lo pequeño y repetido de Comunicaciones.
 *
 * ## Los estados dejan de llevar el color escrito a mano
 *
 * El vanilla los tenía así (`page-communications.js:18-19`):
 *
 *     borrador: ['#7F8C8D', 'Borrador', 'var(--text-muted)'],
 *     programada: ['#F5A623', 'Programada', 'var(--orange-text)'], …
 *
 * Seis hexadecimales a mano, y el fondo se montaba concatenando alfa al hex
 * (`background:${col}1f`) — un truco que obliga a que el valor siga siendo hex
 * para siempre, y que por tanto impide que el color salga de un token. Su
 * propio comentario lo explicaba: «el texto necesita el token accesible
 * aparte», porque «Enviada» en verde de marca daba 2,87.
 *
 * `Badge` ya resuelve exactamente eso —tinte del fondo + tinta oscura del mismo
 * tono— y lo hace con tokens que responden al tema oscuro. Aquí solo queda
 * decir qué tono le toca a cada estado.
 */

const TONO: Record<EstadoComunicacion, React.ComponentProps<typeof Badge>['tone']> = {
  borrador: 'neutral',
  programada: 'warn',
  enviando: 'info',
  enviada: 'ok',
  error: 'danger',
};

const ETIQUETA: Record<EstadoComunicacion, string> = {
  borrador: 'Borrador',
  programada: 'Programada',
  enviando: 'Enviando',
  enviada: 'Enviada',
  error: 'Error',
};

/** El estado de una comunicación, siempre con su palabra.
 *
 *  Un estado desconocido se muestra tal cual en tono neutro en vez de
 *  desaparecer: si el servidor añade un estado nuevo, es mejor leerlo sin
 *  estilo que no verlo. */
export function EstadoDeComunicacion({ estado }: { estado: EstadoComunicacion }) {
  return <Badge tone={TONO[estado] ?? 'neutral'}>{ETIQUETA[estado] ?? estado}</Badge>;
}

/* ── Canales ───────────────────────────────────────────────────────────── */

export const CANALES: Array<{ clave: Canal; icono: string; etiqueta: string; ayuda: string }> = [
  { clave: 'feed', icono: 'bi-megaphone', etiqueta: 'Feed', ayuda: 'Aparece en el muro de Novedades' },
  { clave: 'email', icono: 'bi-envelope', etiqueta: 'Correo', ayuda: 'Llega al correo corporativo' },
  { clave: 'notif', icono: 'bi-bell', etiqueta: 'Buzón interno', ayuda: 'Aparece en la campana del portal' },
];

/** Los iconos de los canales por los que salió una comunicación.
 *
 *  Un icono suelto no dice nada a quien no lo ve: el vanilla pintaba
 *  `<i class="bi bi-envelope">` sin texto alternativo ninguno. Aquí los iconos
 *  son decorativos (`aria-hidden`) y el nombre de los canales viaja en un
 *  `.sr-only` que se lee de corrido. */
export function IconosDeCanal({ canales }: { canales: Canal[] }) {
  const conocidos = CANALES.filter((c) => canales.includes(c.clave));
  if (!conocidos.length) return null;
  return (
    <>
      <span className="inline-flex gap-1.5" aria-hidden="true">
        {conocidos.map((c) => (
          <i key={c.clave} className={cn('bi', c.icono)} title={c.etiqueta} />
        ))}
      </span>
      <span className="sr-only">Enviada por {conocidos.map((c) => c.etiqueta).join(', ')}</span>
    </>
  );
}
